import LandingNavigation from './landing/LandingNavigation';
import './portal/portalTheme.css';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Calendar, 
  User, 
  Clock, 
  ArrowLeft, 
  Tag,
  ChevronRight,
  Newspaper,
  Search,
  Share2,
  Copy,
  Check,
  Link as LinkIcon
} from 'lucide-react';
import Logo from './Logo';
import LandingFooter from './LandingFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Blog = ({ onBack, onLogin, onRegister, navigateTo, initialSlug }) => {
  const [blogs, setBlogs] = useState([]);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Check URL for blog slug on mount
    const pathParts = window.location.pathname.split('/');
    const slugFromUrl = pathParts.length > 2 && pathParts[1] === 'blog' ? pathParts[2] : null;
    
    if (slugFromUrl || initialSlug) {
      fetchBlogBySlug(slugFromUrl || initialSlug);
    }
    fetchBlogs();
  }, [initialSlug]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/blog/posts`);
      setBlogs(response.data.blogs || []);
    } catch (err) {
      console.error('Failed to fetch blogs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBlogBySlug = async (slug) => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/blog/posts/${slug}`);
      setSelectedBlog(response.data);
      // Update URL without reload for SEO and shareability
      window.history.pushState({}, '', `/blog/${slug}`);
    } catch (err) {
      console.error('Failed to fetch blog:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToBlogList = () => {
    setSelectedBlog(null);
    // Reset URL to /blog
    window.history.pushState({}, '', '/blog');
  };

  const getBlogUrl = (slug) => {
    // Generate the full shareable URL
    const baseUrl = window.location.origin;
    return `${baseUrl}/blog/${slug}`;
  };

  const copyBlogUrl = async () => {
    if (!selectedBlog?.slug) return;
    const url = getBlogUrl(selectedBlog.slug);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getReadingTime = (content) => {
    if (!content) return '5 min read';
    const words = content.split(/\s+/).length;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min read`;
  };

  const getAllTags = () => {
    const tags = new Set();
    blogs.forEach(blog => {
      (blog.tags || []).forEach(tag => tags.add(tag));
    });
    return Array.from(tags);
  };

  const filteredBlogs = blogs.filter(blog => {
    const matchesSearch = !searchTerm || 
      blog.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTag = !selectedTag || (blog.tags || []).includes(selectedTag);
    return matchesSearch && matchesTag;
  });

  // Blog Detail View
  if (selectedBlog) {
    return (
      <div className="lexa-portal lexa-portal-content min-h-screen">
        {/* Header */}
      <LandingNavigation onHome={onBack} onLogin={onLogin} onRegister={onRegister} navigateTo={navigateTo} productTarget="/features" />

        {/* Blog Content */}
        <article className="max-w-4xl mx-auto px-4 py-12">
          {/* Back to Blog */}
          <button
            onClick={handleBackToBlogList}
            className="flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-8 font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </button>

          {/* Thumbnail */}
          {selectedBlog.thumbnail_base64 && (
            <img
              src={`data:image/png;base64,${selectedBlog.thumbnail_base64}`}
              alt={selectedBlog.title}
              className="w-full h-72 md:h-96 object-cover rounded-2xl mb-8"
            />
          )}

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            {(selectedBlog.tags || []).map(tag => (
              <span
                key={tag}
                className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium"
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {selectedBlog.title}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-gray-500 text-sm mb-8 pb-8 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span>{selectedBlog.editor_name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(selectedBlog.published_date || selectedBlog.created_at)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{getReadingTime(selectedBlog.content)}</span>
            </div>
            {/* Share Button */}
            <button
              onClick={copyBlogUrl}
              className="flex items-center gap-2 ml-auto px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              data-testid="copy-blog-url-btn"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-green-600 font-medium">Copied!</span>
                </>
              ) : (
                <>
                  <LinkIcon className="w-4 h-4" />
                  <span>Copy Link</span>
                </>
              )}
            </button>
          </div>

          {/* Shareable URL Display */}
          <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-500 mb-1">Share this article:</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm text-purple-600 bg-white px-3 py-2 rounded border border-gray-200 overflow-x-auto">
                {getBlogUrl(selectedBlog.slug)}
              </code>
              <button
                onClick={copyBlogUrl}
                className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex-shrink-0"
                data-testid="copy-url-btn"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            {selectedBlog.content?.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-gray-600 mb-4 leading-relaxed">
                {paragraph.replace(/^#+ /, '').trim()}
              </p>
            ))}
          </div>

          {/* SEO Keywords */}
          {selectedBlog.seo_keywords && selectedBlog.seo_keywords.length > 0 && (
            <div className="mt-12 p-6 bg-gray-50 rounded-xl">
              <h4 className="font-semibold text-gray-700 mb-3">Related Topics</h4>
              <div className="flex flex-wrap gap-2">
                {selectedBlog.seo_keywords.map(keyword => (
                  <span
                    key={keyword}
                    className="px-3 py-1 bg-white border border-gray-200 text-gray-600 rounded-full text-sm"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl text-center text-white">
            <h3 className="text-2xl font-bold mb-2">Ready to automate your testing?</h3>
            <p className="text-purple-100 mb-6">Start your free trial and ship faster with AI-powered test automation.</p>
            <button
              onClick={onRegister}
              className="px-6 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Get Started Free
            </button>
          </div>
        </article>

        <LandingFooter navigateTo={navigateTo} />
      </div>
    );
  }

  // Blog List View
  return (
    <div className="lexa-portal lexa-portal-content min-h-screen">
      {/* Header */}
      <LandingNavigation onHome={onBack} onLogin={onLogin} onRegister={onRegister} navigateTo={navigateTo} productTarget="/features" />

      {/* Hero Section */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-6">
            <Newspaper className="w-4 h-4" />
            Lexa Blog
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            QA & Testing Insights
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Stay up-to-date with the latest trends in software testing, QA automation, and AI-powered testing solutions.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          {/* Search */}
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
            />
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedTag(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                !selectedTag
                  ? 'bg-purple-600 text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              All
            </button>
            {getAllTags().slice(0, 5).map(tag => (
              <button
                key={tag}
                onClick={() => setSelectedTag(tag)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedTag === tag
                    ? 'bg-purple-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Blog Grid */}
      <div className="max-w-7xl mx-auto px-4 pb-16 sm:px-6 lg:px-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredBlogs.length === 0 ? (
          <div className="text-center py-20">
            <Newspaper className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No articles found</h3>
            <p className="text-gray-500">Check back soon for new content!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredBlogs.map(blog => (
              <article
                key={blog.id}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => fetchBlogBySlug(blog.slug)}
              >
                {/* Thumbnail */}
                <div className="aspect-video bg-gradient-to-br from-purple-100 to-indigo-100 relative overflow-hidden">
                  {blog.thumbnail_base64 ? (
                    <img
                      src={`data:image/png;base64,${blog.thumbnail_base64}`}
                      alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Newspaper className="w-12 h-12 text-purple-300" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(blog.tags || []).slice(0, 2).map(tag => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-purple-50 text-purple-600 rounded text-xs font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Title */}
                  <h2 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-purple-600 transition-colors">
                    {blog.title}
                  </h2>

                  {/* Excerpt */}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {blog.excerpt || blog.meta_description}
                  </p>

                  {/* Meta */}
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                        <User className="w-3 h-3 text-purple-600" />
                      </div>
                      <span>{blog.editor_name}</span>
                    </div>
                    <span>{formatDate(blog.published_date || blog.created_at)}</span>
                  </div>
                </div>

                {/* Read More */}
                <div className="px-6 pb-6">
                  <div className="flex items-center gap-1 text-purple-600 font-medium text-sm group-hover:gap-2 transition-all">
                    Read More
                    <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to transform your testing?</h2>
          <p className="text-purple-100 text-lg mb-8">
            Join thousands of QA teams using AI-powered test automation to ship faster.
          </p>
          <button
            onClick={onRegister}
            className="px-8 py-4 bg-white text-purple-600 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
          >
            Start Your Free Trial
          </button>
        </div>
      </div>

      <LandingFooter navigateTo={navigateTo} />
    </div>
  );
};

export default Blog;
